import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./EndToEndSiliconDevelopment.css";
import siliconImage from "../../../images/engineering-capability-hero.png";

type SolutionItem = { title: string; text: string };
type SolutionSection = {
  title: string;
  paragraphs: string[];
  items?: SolutionItem[];
  listTitle?: string;
};
type FrameworkSection = SolutionSection & {
  frameworkTitle?: string;
  principles?: string[];
};
type SolutionConfig = {
  pageTitle: string;
  variant: "team" | "ownership" | "lifecycle" | "productivity" | "realization";
  hero: { title: string; paragraphs: string[]; cta: string };
  overview: SolutionSection;
  framework: FrameworkSection;
  why: SolutionSection;
  cta: { title: string; paragraphs: string[]; cta: string };
};

const config: SolutionConfig = {
  "pageTitle": "End-to-End Silicon Development",
  "hero": {
    "title": "Engineering the Complete Silicon Journey.",
    "paragraphs": [
      "Developing successful semiconductor products requires more than excellence in individual engineering disciplines. It demands seamless collaboration across the entire silicon development lifecycle, where every stage builds on the success of the one before it. Managing this complexity requires an engineering partner capable of integrating multidisciplinary expertise, maintaining execution continuity, and driving consistent progress from concept to product realization.",
      "Digicomm Semiconductor delivers End-to-End Silicon Development solutions that bring together design, verification, implementation, validation, software, and product engineering within a unified execution framework. By combining deep technical expertise with disciplined program execution, we help customers accelerate development, reduce integration risk, and deliver reliable silicon with greater confidence."
    ],
    "cta": "Discuss Your Silicon Development Program"
  },
  "overview": {
    "title": "Integrated Engineering Across the Silicon Development Lifecycle",
    "paragraphs": [
      "As semiconductor products become increasingly sophisticated, successful development depends on seamless coordination across multiple engineering disciplines. Managing separate teams, vendors, and workflows can introduce communication gaps, integration challenges, and execution risks that impact development schedules and product quality. A unified engineering approach helps maintain continuity, improve collaboration, and streamline execution throughout the silicon development lifecycle.",
      "Digicomm Semiconductor brings together multidisciplinary engineering expertise within a single execution framework, enabling customers to manage complex development programs with greater efficiency and confidence. By integrating engineering capabilities across every stage of the lifecycle, we help reduce coordination overhead, improve engineering alignment, and accelerate the journey from architecture to product realization."
    ],
    "listTitle": "Ideal Engagement Scenarios",
    "items": [
      {
        "title": "Complete Silicon Development Programs",
        "text": "Execute semiconductor development initiatives through a single engineering partner with integrated lifecycle expertise."
      },
      {
        "title": "Complex Multi-Disciplinary Projects",
        "text": "Coordinate engineering activities across design, verification, implementation, validation, software, and product engineering within a unified delivery framework."
      },
      {
        "title": "Reduced Integration Risk",
        "text": "Minimize engineering handoffs, improve cross-functional collaboration, and maintain continuity throughout the development lifecycle."
      },
      {
        "title": "Accelerated Time-to-Delivery",
        "text": "Streamline program execution through synchronized engineering activities, reducing delays caused by fragmented workflows and multiple vendor coordination."
      },
      {
        "title": "Scalable Long-Term Engineering Partnership",
        "text": "Establish a strategic engineering relationship capable of supporting multiple product generations, evolving technology requirements, and future semiconductor development initiatives."
      }
    ]
  },
  "framework": {
    "title": "One Engineering Framework. Every Stage Connected.",
    "paragraphs": [
      "Delivering successful silicon requires more than excellence within individual engineering disciplines. It requires every phase of development to be connected through shared objectives, coordinated execution, and continuous collaboration. Digicomm's End-to-End Silicon Development framework brings together multidisciplinary engineering teams within a single execution model, enabling seamless transitions between lifecycle stages while maintaining technical consistency, project visibility, and delivery momentum.",
      "By integrating engineering capabilities under one framework, we help customers simplify program management, reduce coordination overhead, and execute complex semiconductor programs with greater confidence."
    ],
    "frameworkTitle": "Our Integrated Delivery Framework",
    "items": [
      {
        "title": "Architecture & Design Foundation",
        "text": "Establish a strong technical foundation through collaborative planning, architecture alignment, and design implementation that supports long-term product objectives."
      },
      {
        "title": "Verification & Design Assurance",
        "text": "Validate functionality, performance, and design integrity through comprehensive verification methodologies that improve first-pass success."
      },
      {
        "title": "Implementation & Silicon Readiness",
        "text": "Coordinate physical design, timing closure, DFT, and implementation activities to prepare designs for successful silicon realization."
      },
      {
        "title": "Validation & System Enablement",
        "text": "Verify silicon performance, enable hardware platforms, develop embedded software, and support system-level integration to ensure product readiness."
      },
      {
        "title": "Product Realization & Lifecycle Support",
        "text": "Support product engineering, optimization, deployment, and continuous engineering enhancements as products progress toward commercialization and future generations."
      }
    ],
    "principles": [
      "Integrated Multidisciplinary Execution",
      "Lifecycle-Wide Engineering Continuity",
      "Cross-Functional Collaboration",
      "Unified Program Governance",
      "Predictable Delivery Milestones",
      "Continuous Quality Focus"
    ]
  },
  "why": {
    "title": "Why Organizations Choose Digicomm for End-to-End Silicon Development",
    "paragraphs": [
      "Delivering successful semiconductor products requires more than excellence in individual engineering disciplines. It requires an engineering partner capable of connecting every phase of development into a coordinated, efficient, and predictable execution model. Digicomm combines multidisciplinary semiconductor expertise with structured program execution, enabling customers to manage complex development programs through a single engineering partner committed to quality, continuity, and delivery excellence."
    ],
    "listTitle": "Our Differentiators",
    "items": [
      {
        "title": "Integrated Lifecycle Expertise",
        "text": "Comprehensive engineering capabilities spanning architecture, design, verification, implementation, validation, software, and product engineering ensure continuity across the complete silicon development lifecycle."
      },
      {
        "title": "Unified Engineering Execution",
        "text": "A single delivery framework brings together multidisciplinary teams, reducing coordination complexity and improving collaboration across every stage of development."
      },
      {
        "title": "Seamless Cross-Functional Collaboration",
        "text": "Engineering disciplines work in alignment through shared objectives, integrated planning, and continuous communication, minimizing handoff risks and improving execution efficiency."
      },
      {
        "title": "Disciplined Program Governance",
        "text": "Structured planning, milestone-based execution, transparent reporting, and proactive risk management provide customers with complete visibility and confidence throughout the program."
      },
      {
        "title": "Scalable Engineering Partnership",
        "text": "Whether supporting a single product or multiple development programs, our engagement model scales to meet evolving business priorities and long-term technology roadmaps."
      },
      {
        "title": "Focus on Successful Silicon Outcomes",
        "text": "Every engagement is driven by a commitment to engineering quality, execution excellence, and delivering reliable silicon that meets customer objectives for performance, schedule, and product readiness."
      }
    ]
  },
  "cta": {
    "title": "Engineer Your Next Silicon Program with Confidence",
    "paragraphs": [
      "Successful silicon development depends on more than individual engineering expertise—it requires a partner who can connect every stage of the development lifecycle through integrated execution, multidisciplinary collaboration, and disciplined program delivery. Whether you're developing a new product, scaling an existing platform, or managing a complex semiconductor roadmap, Digicomm Semiconductor provides the engineering capabilities and lifecycle expertise to help transform your vision into reliable silicon."
    ],
    "cta": "Discuss Your Silicon Development Program"
  },
  "variant": "lifecycle"
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TechIcon = ({ index = 0 }: { index?: number }) => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    {index % 5 === 0 && <>
      <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="19" y="19" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 7v6M24 7v6M30 7v6M18 35v6M24 35v6M30 35v6M7 18h6M7 24h6M7 30h6M35 18h6M35 24h6M35 30h6" stroke="currentColor" strokeWidth="1.6"/>
    </>}
    {index % 5 === 1 && <>
      <circle cx="24" cy="17" r="6" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="36" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M13 39c1-8 5-12 11-12s10 4 11 12M4 37c1-6 4-9 9-9M44 37c-1-6-4-9-9-9" fill="none" stroke="currentColor" strokeWidth="1.7"/>
    </>}
    {index % 5 === 2 && <>
      <path d="M9 34l9-10 8 6 13-17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M32 13h7v7" fill="none" stroke="currentColor" strokeWidth="2"/>
    </>}
    {index % 5 === 3 && <>
      <path d="M24 6l14 6v10c0 10-6 16-14 20C16 38 10 32 10 22V12z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 24l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
    {index % 5 === 4 && <>
      <circle cx="24" cy="24" r="7" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M37 11l-4 4M15 33l-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
  </svg>
);

const Paragraphs = ({ items }: { items: string[] }) => (
  <>{items.map((p, i) => <p key={i}>{p}</p>)}</>
);

const HeroVisual = () => (
  <div className="sol-hero-visual lifecycle-hero-artwork">
    <img src={siliconImage} alt="Layered semiconductor chip and connected engineering circuitry" />
  </div>
);
const FrameworkVisual = () => {
  const items = config.framework.items || [];
  return (
    <div className={`sol-framework-visual sol-framework-visual--${config.variant}`} aria-hidden="true">
      <div className="sol-framework-center">
        <TechIcon index={config.variant === "team" ? 1 : config.variant === "ownership" ? 3 : config.variant === "productivity" ? 4 : 0}/>
      </div>
      {items.slice(0, 6).map((_, i) => (
        <span key={i} className={`sol-orbit sol-orbit--${i + 1}`}>
          <i>{String(i + 1).padStart(2, "0")}</i>
        </span>
      ))}
    </div>
  );
};

const Page: React.FC = () => (
  <Fragment>
    <div className={`solution-experience solution-experience--${config.variant} inner-page`}>
      <div className="body_wrap o-clip">
        <Header />
        <main>
          <PageTitle pageTitle={config.pageTitle} pagesub={config.pageTitle} />

          <section className="sol-hero">
            <div className="sol-shell sol-hero-layout">
              <div className="sol-copy sol-hero-copy">
                <h1>{config.pageTitle}</h1>
                <h2>{config.hero.title}</h2>
                <Paragraphs items={config.hero.paragraphs}/>
                {config.hero.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.hero.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <HeroVisual />
            </div>
          </section>

          <section className="sol-section sol-overview">
            <div className="sol-shell">
              <div className="sol-heading-layout">
                <div className="sol-copy"><h2>{config.overview.title}</h2></div>
                <div className="sol-copy"><Paragraphs items={config.overview.paragraphs}/></div>
              </div>
              {config.overview.listTitle && <h3 className="sol-list-heading">{config.overview.listTitle}</h3>}
              <div className="sol-scenario-grid">
                {(config.overview.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span className="sol-index">{String(i + 1).padStart(2, "0")}</span>
                    <div className="sol-icon"><TechIcon index={i}/></div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="sol-section sol-framework">
            <div className="sol-shell">
              <div className="sol-framework-top">
                <div className="sol-copy">
                  <h2>{config.framework.title}</h2>
                  <Paragraphs items={config.framework.paragraphs}/>
                </div>
                <FrameworkVisual />
              </div>

              {config.framework.frameworkTitle && <h3 className="sol-list-heading">{config.framework.frameworkTitle}</h3>}

              <div className="sol-framework-steps">
                {(config.framework.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <div><h3>{item.title}</h3><p>{item.text}</p></div>
                  </article>
                ))}
              </div>

              {!!config.framework.principles?.length && (
                <div className="sol-principles">
                  {config.framework.principles.map((x) => <span key={x}>{x}</span>)}
                </div>
              )}
            </div>
          </section>

          <section className="sol-section sol-why">
            <div className="sol-shell sol-why-layout">
              <div className="sol-copy">
                <h2>{config.why.title}</h2>
                <Paragraphs items={config.why.paragraphs}/>
              </div>

              <div className={`sol-value-system sol-value-system--${config.variant}`}>
                <div className="sol-value-center">
                  <TechIcon index={config.variant === "productivity" ? 4 : config.variant === "team" ? 1 : 0}/>
                  <strong>{config.pageTitle}</strong>
                </div>
                <div className="sol-value-grid">
                  {(config.why.items || []).map((item, i) => (
                    <article key={item.title}>
                      <div className="sol-icon"><TechIcon index={i}/></div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="sol-cta">
            <div className="sol-shell sol-cta-layout">
              <div className="sol-copy">
                <h2>{config.cta.title}</h2>
                <Paragraphs items={config.cta.paragraphs}/>
                {config.cta.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.cta.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <div className="sol-cta-visual" aria-hidden="true">
                <div className="sol-globe"><i/><i/><i/><i/><i/></div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <Scrollbar />
      </div>
    </div>
  </Fragment>
);

export default Page;
